import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Table, Tag, Tooltip, Spin } from "antd";
import ReactECharts from "echarts-for-react";
import { useLazyQuery } from "@apollo/client";
import { GET_SALES_DASHBOARD } from "../../../Queries/SalesDashboard";
import { GET_OPERATIONS_DASHBOARD } from "../../../Queries/OperationsDashboard";
import {
  T, statusColor, gradients, getGradient, fmtHrs, fmtSec,
  KpiCard, SectionHeader, RingProgress, RefreshButton, DatePickerBar,
  SelfDashboard,
} from "./OpsDashboardShared";

// ─── Sales Self Dashboard (single rep detail view) ──────────────────────────

export const SalesSelfDashboard = ({ emp, opsEmp }) => {
  if (!emp) return <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>No data found</div>;

  // Activity chart — Today / Week / Month
  const activityChart = {
    tooltip: { trigger: "axis", backgroundColor: "#fff", borderColor: T.border, textStyle: { color: T.text, fontSize: 12 } },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: T.textSecondary } },
    grid: { top: 20, right: 20, bottom: 40, left: 50 },
    xAxis: { type: "category", data: ["Today", "This Week", "This Month"], axisLine: { lineStyle: { color: T.border } }, axisLabel: { fontSize: 11, color: T.textSecondary } },
    yAxis: { type: "value", name: "Count", nameTextStyle: { fontSize: 11, color: T.textMuted }, axisLine: { show: false }, splitLine: { lineStyle: { color: T.borderLight } }, axisLabel: { fontSize: 11, color: T.textSecondary } },
    series: [
      { name: "Calls", type: "bar", barWidth: 20, itemStyle: { color: T.indigo, borderRadius: [4, 4, 0, 0] }, data: [emp.callsToday, emp.callsThisWeek, emp.callsThisMonth] },
      { name: "Emails", type: "bar", barWidth: 20, itemStyle: { color: T.emerald, borderRadius: [4, 4, 0, 0] }, data: [emp.emailsToday, emp.emailsThisWeek, emp.emailsThisMonth] },
      { name: "LinkedIn", type: "bar", barWidth: 20, itemStyle: { color: T.sky, borderRadius: [4, 4, 0, 0] }, data: [emp.linkedinToday, emp.linkedinThisWeek, emp.linkedinThisMonth] },
    ],
  };

  // Pipeline stage donut
  const stageData = (emp.stageBreakdown || []).map((s) => ({ name: s.stage, value: s.count }));
  const stageChart = {
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
    legend: { bottom: 0, textStyle: { fontSize: 10, color: T.textSecondary } },
    series: [{
      type: "pie", radius: ["45%", "70%"], center: ["50%", "45%"],
      itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
      label: { show: false },
      data: stageData.length ? stageData : [{ name: "No Pipeline", value: 1 }],
      color: [T.indigo, T.emerald, T.amber, T.sky, T.violet, T.rose],
    }],
  };

  // Priority breakdown donut
  const priorityColors = { High: T.rose, Medium: T.amber, Low: T.emerald, Unset: T.textMuted };
  const priorityData = (emp.priorityBreakdown || []).map((p) => ({ name: p.priority, value: p.count }));
  const priorityChart = {
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
    legend: { bottom: 0, textStyle: { fontSize: 10, color: T.textSecondary } },
    series: [{
      type: "pie", radius: ["45%", "70%"], center: ["50%", "45%"],
      itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
      label: { show: false },
      data: priorityData.length ? priorityData : [{ name: "No Leads", value: 1 }],
      color: priorityData.map((p) => priorityColors[p.name] || T.slate),
    }],
  };

  const totalActivitiesToday = emp.callsToday + emp.emailsToday + emp.linkedinToday;

  return (
    <div>
      {/* Lead Overview KPIs */}
      <SectionHeader title="Lead Overview" badge="Leads" badgeColor={T.indigo} />
      <div className="ops-grid ops-grid-4" style={{ marginBottom: 20 }}>
        <KpiCard label="Assigned Leads" value={emp.totalLeadsAssigned} color={T.indigo} gradient={gradients[0]} delay={0} />
        <KpiCard label="New Today" value={emp.leadsCreatedToday} sub={`${emp.leadsCreatedThisWeek} this week`} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={60} />
        <KpiCard label="New This Month" value={emp.leadsCreatedThisMonth} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={120} />
        <KpiCard label="Pipeline Deals" value={emp.pipelineDeals} sub={`$${(emp.totalDealValue || 0).toLocaleString()}`} color={T.violet} gradient={gradients[5]} delay={180} />
      </div>

      {/* Activity KPIs */}
      <SectionHeader title="Sales Activities — Today" badge="Outreach" badgeColor={T.emerald} />
      <div className="ops-grid ops-grid-4" style={{ marginBottom: 20 }}>
        <KpiCard label="Calls" value={emp.callsToday} sub={`${emp.callsThisMonth} this month`} color={T.indigo} gradient={gradients[0]} delay={0} />
        <KpiCard label="Emails" value={emp.emailsToday} sub={`${emp.emailsThisMonth} this month`} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={60} />
        <KpiCard label="LinkedIn" value={emp.linkedinToday} sub={`${emp.linkedinThisMonth} this month`} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={120} />
        <KpiCard label="Total Activities" value={totalActivitiesToday} color={T.violet} gradient={gradients[5]} delay={180} />
      </div>

      {/* Activity Chart */}
      <div className="ops-grid ops-grid-2" style={{ marginBottom: 20 }}>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Activity Trend</div>
          <ReactECharts option={activityChart} style={{ height: 240 }} />
        </div>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Pipeline Stages</div>
          <ReactECharts option={stageChart} style={{ height: 240 }} />
        </div>
      </div>

      {/* Pipeline & Follow-ups */}
      <SectionHeader title="Pipeline & Follow-ups" badge="Deals" badgeColor={T.amber} />
      <div className="ops-grid ops-grid-6" style={{ marginBottom: 20 }}>
        <KpiCard label="Closed Won" value={emp.closedWon} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={0} />
        <KpiCard label="Closed Lost" value={emp.closedLost} color={T.rose} gradient="linear-gradient(135deg, #f43f5e, #e11d48)" delay={60} />
        <KpiCard label="Follow-ups Total" value={emp.followUpsTotal} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={120} />
        <KpiCard label="Due Today" value={emp.followUpsDueToday} color={T.amber} gradient="linear-gradient(135deg, #f59e0b, #f97316)" delay={180} />
        <KpiCard label="Overdue" value={emp.followUpsOverdue} color={T.rose} gradient="linear-gradient(135deg, #f43f5e, #e11d48)" delay={240} />
        <KpiCard label="Ops Calls" value={emp.opsCallsScheduled} sub={`${emp.opsCallsConnected} connected`} color={T.violet} gradient={gradients[5]} delay={300} />
      </div>

      {/* Priority Breakdown */}
      <div className="ops-grid ops-grid-2" style={{ marginBottom: 20 }}>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 12 }}>Lead Priority</div>
          <ReactECharts option={priorityChart} style={{ height: 220 }} />
        </div>
        <div className="ops-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 16 }}>Scheduled Calls Today</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 180 }}>
            <div style={{ textAlign: "center" }}>
              <RingProgress percent={emp.scheduledCallsToday > 0 ? 100 : 0} color={T.indigo} size={80} strokeWidth={6} />
              <div style={{ fontSize: 32, fontWeight: 800, color: T.indigo, marginTop: 8 }}>{emp.scheduledCallsToday}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Scheduled for Today</div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ Work & Activity (from Operations data) ═══════════ */}
      {opsEmp && (
        <>
          <SectionHeader title="Work & Activity" badge="Timers" badgeColor={T.indigo} subtitle="Work hours, desktop activity, and productivity" />

          {/* Timer vs Desktop comparison */}
          <div className="ops-card" style={{ padding: "20px 24px", marginBottom: 20, background: `linear-gradient(135deg, ${T.indigoLight}, ${T.skyLight})`, border: `1px solid ${T.indigo}20` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>Timer vs Desktop Activity — Today</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 20, alignItems: "center" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.indigo, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Work Timer</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: T.indigo, lineHeight: 1 }}>{fmtHrs(opsEmp.hoursToday)}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Logged via timer</div>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: T.emerald, fontWeight: 600 }}>{fmtHrs(opsEmp.productiveToday)} productive</span>
                  <span style={{ fontSize: 11, color: T.rose, fontWeight: 600 }}>{fmtHrs(opsEmp.nonProductiveToday)} non-prod</span>
                </div>
              </div>
              <div style={{ width: 1, height: 70, background: T.border, position: "relative" }}>
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", background: T.card, padding: "4px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, color: T.textMuted, border: `1px solid ${T.border}` }}>VS</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.sky, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Desktop Activity</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: T.sky, lineHeight: 1 }}>{fmtHrs((opsEmp.totalActive || 0) / 3600)}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>Active on desktop</div>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: T.emerald, fontWeight: 600 }}>{fmtSec(opsEmp.activityProductive)} productive</span>
                  <span style={{ fontSize: 11, color: T.amber, fontWeight: 600 }}>{fmtSec(opsEmp.activityUnproductive)} unprod</span>
                </div>
              </div>
            </div>
          </div>

          {/* Work Hours KPIs */}
          <div className="ops-grid ops-grid-4" style={{ marginBottom: 16 }}>
            <KpiCard label="Today" value={fmtHrs(opsEmp.hoursToday)} color={T.indigo} gradient={gradients[0]} delay={0} />
            <KpiCard label="This Week" value={fmtHrs(opsEmp.hoursThisWeek)} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={80} />
            <KpiCard label="This Month" value={fmtHrs(opsEmp.hoursThisMonth)} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={160} />
            <KpiCard label="This Year" value={fmtHrs(opsEmp.hoursThisYear)} color={T.violet} gradient="linear-gradient(135deg, #8b5cf6, #a855f7)" delay={240} />
          </div>

          {/* Desktop Activity KPIs */}
          <div className="ops-grid ops-grid-4" style={{ marginBottom: 16 }}>
            <KpiCard label="Active Time" value={fmtSec(opsEmp.totalActive)} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={0} />
            <KpiCard label="Idle Time" value={fmtSec(opsEmp.totalIdle)} color={T.amber} gradient="linear-gradient(135deg, #f59e0b, #f97316)" delay={80} />
            <KpiCard label="Focus Switches" value={opsEmp.focusSwitches || 0} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={160} />
            <KpiCard label="Breaks" value={opsEmp.breakCount || 0} color={T.rose} gradient="linear-gradient(135deg, #f43f5e, #e11d48)" delay={240} />
          </div>

          {/* Task breakdown + Top Apps */}
          <div className="ops-grid ops-grid-2" style={{ marginBottom: 20 }}>
            <div className="ops-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Task Breakdown (This Month)</div>
              {(opsEmp.taskBreakdown || []).length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(opsEmp.taskBreakdown || []).slice(0, 8).map((task, i) => (
                    <div key={task.taskType} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: i === 0 ? T.indigoLight : "transparent" }}>
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.text, textTransform: "capitalize" }}>{task.taskType}</span>
                        <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 8 }}>{task.sessionCount} sessions</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 800, color: T.indigo }}>{fmtHrs(task.totalHours)}</span>
                    </div>
                  ))}
                </div>
              ) : <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>No task data</div>}
            </div>
            <div className="ops-card" style={{ padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>Top Applications</div>
              {(opsEmp.topApps || []).length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(opsEmp.topApps || []).slice(0, 8).map((app, i) => {
                    const colors = [T.indigo, T.sky, T.emerald, T.violet, T.amber, T.teal, T.rose, "#f97316"];
                    return (
                      <div key={app.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border}` }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{app.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: colors[i % 8] }}>{fmtSec(app.duration)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>No desktop data</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Sales Admin Dashboard ──────────────────────────────────────────────────

const SalesAdminDashboard = ({ employees, teamSummary, opsMap }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const columns = [
    {
      title: "Sales Rep",
      dataIndex: "employeeName",
      key: "employeeName",
      width: 200,
      fixed: "left",
      render: (val, rec) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <div className="ops-avatar" style={{ background: getGradient(employees.indexOf(rec)) }}>{(val || "?")[0]?.toUpperCase()}</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: T.text }}>{val}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>
              {rec.isPrimarySales === "Yes" ? rec.designation || "Sales" : <span style={{ color: T.amber }}>Additional Role</span>}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "currentStatus",
      key: "currentStatus",
      width: 100,
      render: (val) => (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <span className={`ops-status-dot ${statusColor(val)}`} />
          <span style={{ fontSize: 12, fontWeight: 500, color: T.textSecondary }}>{val || "Offline"}</span>
        </span>
      ),
    },
    {
      title: "Assigned Leads",
      dataIndex: "totalLeadsAssigned",
      key: "totalLeadsAssigned",
      width: 80,
      sorter: (a, b) => a.totalLeadsAssigned - b.totalLeadsAssigned,
      defaultSortOrder: "descend",
      render: (val) => <span style={{ fontSize: 13, fontWeight: 700, color: T.indigo }}>{val}</span>,
    },
    {
      title: "Calls",
      dataIndex: "callsToday",
      key: "callsToday",
      width: 80,
      sorter: (a, b) => a.callsToday - b.callsToday,
      render: (val, rec) => (
        <Tooltip title={`Week: ${rec.callsThisWeek} | Month: ${rec.callsThisMonth}`}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{val}</span>
        </Tooltip>
      ),
    },
    {
      title: "Emails",
      dataIndex: "emailsToday",
      key: "emailsToday",
      width: 80,
      sorter: (a, b) => a.emailsToday - b.emailsToday,
      render: (val, rec) => (
        <Tooltip title={`Week: ${rec.emailsThisWeek} | Month: ${rec.emailsThisMonth}`}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{val}</span>
        </Tooltip>
      ),
    },
    {
      title: "LinkedIn",
      dataIndex: "linkedinToday",
      key: "linkedinToday",
      width: 80,
      sorter: (a, b) => a.linkedinToday - b.linkedinToday,
      render: (val) => <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{val}</span>,
    },
    {
      title: "Pipeline",
      dataIndex: "pipelineDeals",
      key: "pipelineDeals",
      width: 90,
      sorter: (a, b) => a.pipelineDeals - b.pipelineDeals,
      render: (val, rec) => (
        <Tooltip title={`Deal Value: $${(rec.totalDealValue || 0).toLocaleString()}`}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.violet }}>{val}</span>
        </Tooltip>
      ),
    },
    {
      title: "Won / Lost",
      key: "wonLost",
      width: 110,
      render: (_, rec) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Tag color="success" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.closedWon}</Tag>
          <Tag color="error" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.closedLost}</Tag>
        </div>
      ),
    },
    {
      title: "Follow-ups",
      key: "followups",
      width: 110,
      render: (_, rec) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title="Due Today"><Tag color="warning" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.followUpsDueToday}</Tag></Tooltip>
          {rec.followUpsOverdue > 0 && <Tooltip title="Overdue"><Tag color="error" style={{ borderRadius: 20, fontSize: 11, fontWeight: 700, border: "none" }}>{rec.followUpsOverdue}</Tag></Tooltip>}
        </div>
      ),
    },
    {
      title: "Hours Today",
      key: "hoursToday",
      width: 100,
      sorter: (a, b) => (opsMap[a.userId]?.hoursToday || 0) - (opsMap[b.userId]?.hoursToday || 0),
      render: (_, rec) => {
        const ops = opsMap[rec.userId];
        return ops ? (
          <Tooltip title={`Productive: ${fmtHrs(ops.productiveToday)} | Non-Prod: ${fmtHrs(ops.nonProductiveToday)}`}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.indigo }}>{fmtHrs(ops.hoursToday)}</span>
          </Tooltip>
        ) : <span style={{ fontSize: 12, color: T.textMuted }}>—</span>;
      },
    },
    {
      title: "Desktop",
      key: "desktop",
      width: 100,
      render: (_, rec) => {
        const ops = opsMap[rec.userId];
        if (!ops) return <span style={{ fontSize: 12, color: T.textMuted }}>—</span>;
        const activeSec = ops.totalActive || 0;
        const idleSec = ops.totalIdle || 0;
        const totalSec = activeSec + idleSec;
        return (
          <Tooltip title={`Active: ${fmtSec(activeSec)} | Idle: ${fmtSec(idleSec)}`}>
            <span style={{ fontSize: 13, fontWeight: 700, color: T.sky }}>{fmtSec(totalSec)}</span>
          </Tooltip>
        );
      },
    },
  ];

  // Activity bar chart — top 12 reps
  const topRepsChart = {
    tooltip: { trigger: "axis", backgroundColor: "#fff", borderColor: T.border, textStyle: { color: T.text, fontSize: 12 } },
    legend: { bottom: 0, textStyle: { fontSize: 11, color: T.textSecondary } },
    grid: { top: 20, right: 20, bottom: 40, left: 50 },
    xAxis: {
      type: "category",
      data: employees.slice(0, 12).map((e) => e.employeeName?.split(" ")[0] || ""),
      axisLabel: { fontSize: 10, color: T.textSecondary, rotate: 30 },
      axisLine: { lineStyle: { color: T.border } },
    },
    yAxis: {
      type: "value", name: "Count",
      axisLine: { show: false }, splitLine: { lineStyle: { color: T.borderLight } },
      axisLabel: { fontSize: 10, color: T.textSecondary },
    },
    series: [
      { name: "Calls", type: "bar", stack: "total", barWidth: 24, itemStyle: { color: T.indigo }, data: employees.slice(0, 12).map((e) => e.callsToday) },
      { name: "Emails", type: "bar", stack: "total", barWidth: 24, itemStyle: { color: T.emerald }, data: employees.slice(0, 12).map((e) => e.emailsToday) },
      { name: "LinkedIn", type: "bar", stack: "total", barWidth: 24, itemStyle: { color: T.sky, borderRadius: [4, 4, 0, 0] }, data: employees.slice(0, 12).map((e) => e.linkedinToday) },
    ],
  };

  return (
    <div>
      {/* Team Summary KPIs */}
      {teamSummary && (
        <div className="ops-grid ops-grid-6" style={{ marginBottom: 24 }}>
          <KpiCard label="Sales Reps" value={teamSummary.totalReps} color={T.indigo} gradient={gradients[0]} delay={0} />
          <KpiCard label="Assigned Leads" value={teamSummary.totalLeads} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={60} />
          <KpiCard label="New Today" value={teamSummary.newLeadsToday} sub={`${teamSummary.newLeadsThisWeek} this week`} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={120} />
          <KpiCard label="Pipeline" value={teamSummary.pipelineDeals} sub={`$${(teamSummary.pipelineValue || 0).toLocaleString()}`} color={T.violet} gradient={gradients[5]} delay={180} />
          <KpiCard label="Closed Won" value={teamSummary.closedWonCount} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #059669)" delay={240} />
          <KpiCard label="Follow-ups Due" value={teamSummary.followUpsDueToday} color={T.amber} gradient="linear-gradient(135deg, #f59e0b, #f97316)" delay={300} />
        </div>
      )}

      {/* Activity KPI Row */}
      {teamSummary && (
        <>
          <SectionHeader title="Today's Activity" subtitle="Team-wide outreach metrics" />
          <div className="ops-grid ops-grid-3" style={{ marginBottom: 24 }}>
            <KpiCard label="Total Calls" value={teamSummary.totalCallsToday} color={T.indigo} gradient={gradients[0]} delay={0} />
            <KpiCard label="Total Emails" value={teamSummary.totalEmailsToday} color={T.emerald} gradient="linear-gradient(135deg, #10b981, #14b8a6)" delay={60} />
            <KpiCard label="Total LinkedIn" value={teamSummary.totalLinkedinToday} color={T.sky} gradient="linear-gradient(135deg, #0ea5e9, #06b6d4)" delay={120} />
          </div>
        </>
      )}

      {/* Activity Chart */}
      {employees.length > 0 && (
        <>
          <SectionHeader title="Sales Activity — Today" subtitle="Calls, emails, and LinkedIn outreach per rep" />
          <div className="ops-card" style={{ padding: 20, marginBottom: 20 }}>
            <ReactECharts option={topRepsChart} style={{ height: 280 }} />
          </div>
        </>
      )}

      {/* Sales Rep Table */}
      <SectionHeader title="Sales Team" subtitle={`${employees.length} reps`} />
      <div className="ops-card ops-table" style={{ overflow: "hidden" }}>
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="userId"
          size="small"
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ["10", "25", "50", "100"], style: { padding: "12px 16px" } }}
          expandable={{
            expandedRowKeys: expandedRows,
            onExpandedRowsChange: setExpandedRows,
            expandedRowRender: (record) => (
              <div style={{ padding: 20 }}>
                <SalesSelfDashboard emp={record} opsEmp={opsMap[record.userId]} />
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const SalesDashboard = () => {
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const [empStatus, setEmpStatus] = useState("Active");
  const [selectedDate, setSelectedDate] = useState(null);

  const [fetchDashboard, { data, loading, error }] = useLazyQuery(GET_SALES_DASHBOARD, {
    fetchPolicy: "no-cache",
  });

  const [fetchOps, { data: opsData, loading: opsLoading }] = useLazyQuery(GET_OPERATIONS_DASHBOARD, {
    fetchPolicy: "no-cache",
  });

  const loadData = (status, date) => {
    const variables = {
      view: "admin",
      employeestatus: status ?? empStatus,
      loginUserId: loginUser?.id,
    };
    const d = date !== undefined ? date : selectedDate;
    if (d) variables.date = d;
    fetchDashboard({ variables });
    fetchOps({ variables });
  };

  useEffect(() => {
    loadData();
  }, []);

  const dashData = data?.getSalesDashboard;
  const employees = dashData?.employees || [];
  const teamSummary = dashData?.teamSummary;

  // Build ops lookup map: userId → ops employee data
  const opsMap = useMemo(() => {
    const map = {};
    (opsData?.getOperationsDashboard?.employees || []).forEach((e) => {
      map[e.userId] = e;
    });
    return map;
  }, [opsData]);

  const handleStatusChange = (e) => {
    const val = e.target.value;
    setEmpStatus(val);
    loadData(val, selectedDate);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    loadData(empStatus, date);
  };

  const selectStyle = {
    padding: "8px 14px",
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    fontSize: 13,
    color: T.text,
    background: T.card,
    fontWeight: 500,
    outline: "none",
    cursor: "pointer",
    minWidth: 150,
  };

  return (
    <div style={{ padding: "24px 28px", background: T.bg, minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0, letterSpacing: "-0.5px" }}>
            Sales Dashboard
          </h1>
          <p style={{ fontSize: 13, color: T.textSecondary, margin: "4px 0 0" }}>
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <select value={empStatus} onChange={handleStatusChange} style={selectStyle}>
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Terminated">Terminated</option>
            <option value="Resigned">Resigned</option>
          </select>
          <DatePickerBar value={selectedDate} onChange={handleDateChange} />
          <RefreshButton loading={loading} onClick={() => loadData()} />
        </div>
      </div>

      {loading && !dashData && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <Spin size="large" />
        </div>
      )}

      {error && (
        <div className="ops-card" style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.rose, marginBottom: 8 }}>Failed to load dashboard</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>{error.message}</div>
          <button className="ops-refresh-btn" onClick={() => loadData()}>Try Again</button>
        </div>
      )}

      {dashData && !loading && (
        <SalesAdminDashboard employees={employees} teamSummary={teamSummary} opsMap={opsMap} />
      )}
    </div>
  );
};

export default SalesDashboard;
